import React, { useRef, useState, useCallback, useEffect } from 'react';
import { sendMessage, getMessagingDevices } from '../../services/api/messageApi';
import { updateDeviceAttribute } from '../../services/api/deviceApi';
import { RED_FLAG_MESSAGE } from '../../config/redFlagConfig';

const getCarNumber = (device) => {
  const raw = (device.deviceName || device.name || '').trim();
  if (raw.includes('-')) {
    const part = raw.split('-')[0].trim();
    if (part) return part;
  }
  return raw || String(device.deviceId ?? device.id ?? '?');
};

const getDeviceColor = (device, modelColors = {}) => {
  const SOS_COLOR     = '#dc2626';
  const REDFLAG_COLOR = '#9333ea';
  const defaultColor  = modelColors[''] || '#0095d5';

  const attrs = device.attributes || {};
  const isSos     = device.isSosActive === true || attrs.CarStatus === 'SOS';
  const isRedFlag = attrs.CarStatus === 'REDFLAG' || attrs.CarStatus === 'REDFLAG_SEND' || attrs.CarStatus === 'REDFLAG_CONFIRM' || attrs.redflag === true;
  const isOk      = attrs.CarStatus === 'OK';

  if (isSos)     return SOS_COLOR;
  if (isRedFlag) return REDFLAG_COLOR;
  if (isOk)      return 'green';

  const model = device.deviceModel || device.model;
  return modelColors[model] || defaultColor;
};

const sortDevices = (devices) =>
  [...devices].sort((a, b) => {
    const nA = parseInt(getCarNumber(a), 10);
    const nB = parseInt(getCarNumber(b), 10);
    if (!isNaN(nA) && !isNaN(nB)) return nA - nB;
    return getCarNumber(a).localeCompare(getCarNumber(b));
  });

// status: idle | sending | sent | error
const STATUS_ICON = { sent: '✓', error: '✕' };
const STATUS_COLOR = { sent: '#22c55e', error: '#ef4444' };

const AreaSelectionPopup = ({ devices, modelColors = {}, onClose, onDeviceClick, isReadOnly = false }) => {
  const sorted = sortDevices(devices);

  const [pos, setPos] = useState({ x: window.innerWidth - 296, y: 70 });
  const dragState = useRef(null);

  // Red flag state
  const [messagingDevices, setMessagingDevices] = useState([]);
  const [sendStatus, setSendStatus] = useState({}); // { deviceId: 'sending' | 'sent' | 'error' }
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    getMessagingDevices()
      .then(data => setMessagingDevices(data || []))
      .catch(err => console.error('getMessagingDevices error', err));
  }, []);

  const handleSendRedFlag = async () => {
    if (devices.length === 0) return;

    if (!window.confirm(`Send RED FLAG to ${devices.length} vehicle${devices.length > 1 ? 's' : ''}? This is an emergency action.`)) return;

    setIsSending(true);
    const initial = {};
    devices.forEach(d => { initial[d.deviceId ?? d.id] = 'sending'; });
    setSendStatus(initial);

    await Promise.all(devices.map(async (device) => {
      const deviceId = device.deviceId ?? device.id;
      try {
        const traccarId = String(deviceId).trim();
        const msgDevice = messagingDevices.find(n =>
          String(n.DeviceIDTraccar || '').trim() === traccarId
        );

        if (!msgDevice) throw new Error(`No messaging device for ID ${traccarId}`);

        await sendMessage(msgDevice, RED_FLAG_MESSAGE);
        await updateDeviceAttribute(traccarId, 'CarStatus', 'REDFLAG_SEND');
        setSendStatus(prev => ({ ...prev, [deviceId]: 'sent' }));
      } catch (err) {
        console.error(`Red flag failed for ${deviceId}`, err);
        setSendStatus(prev => ({ ...prev, [deviceId]: 'error' }));
      }
    }));

    setIsSending(false);
  };

  const onHeaderMouseDown = useCallback((e) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX - pos.x, startY: e.clientY - pos.y };

    const onMove = (ev) => {
      if (!dragState.current) return;
      setPos({ x: ev.clientX - dragState.current.startX, y: ev.clientY - dragState.current.startY });
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos]);

  const sentCount  = Object.values(sendStatus).filter(s => s === 'sent').length;
  const errorCount = Object.values(sendStatus).filter(s => s === 'error').length;
  const doneCount  = sentCount + errorCount;
  const totalSent  = Object.keys(sendStatus).length;

  return (
    <div
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 2000,
        width: '240px',
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#1e2030',
        color: '#e0e0e0',
        fontSize: '13px',
        userSelect: 'none',
      }}
    >
      {/* Draggable header */}
      <div
        onMouseDown={onHeaderMouseDown}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 10px',
          background: '#2a2d42',
          borderBottom: '1px solid #3a3d55',
          flexShrink: 0,
          cursor: 'grab',
        }}
      >
        <span style={{ fontWeight: 700, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span className="material-icons-outlined" style={{ fontSize: '14px', color: '#888' }}>drag_indicator</span>
          Area &nbsp;
          <span style={{ background: '#3b82f6', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '11px' }}>
            {devices.length}
          </span>
        </span>
        <button
          className="delete is-small"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          style={{ flexShrink: 0 }}
        />
      </div>

      {/* Grid body */}
      <div style={{ overflowY: 'auto', flex: 1, padding: '8px' }}>
        {sorted.length === 0 ? (
          <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '12px' }}>
            No cars in selected area
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
            {sorted.map((device, idx) => {
              const carNumber = getCarNumber(device);
              const bgColor = getDeviceColor(device, modelColors);
              const attrs = device.attributes || {};
              const isRedFlag = attrs.CarStatus === 'REDFLAG' || attrs.CarStatus === 'REDFLAG_SEND' || attrs.CarStatus === 'REDFLAG_CONFIRM' || attrs.redflag === true;
              const model = device.deviceModel || device.model || '';
              const useDarkText = model.toLowerCase() === 'nie aktywny';
              const deviceId = device.deviceId ?? device.id;
              const status = sendStatus[deviceId];

              return (
                <div
                  key={deviceId ?? idx}
                  onClick={() => onDeviceClick(device)}
                  title={device.deviceName || carNumber}
                  className={`${useDarkText ? 'text-black' : 'text-white'} text-[11px] flex items-center justify-center rounded font-bold shadow-sm cursor-pointer hover:opacity-80 hover:scale-105 transition-all overflow-hidden text-ellipsis whitespace-nowrap relative`}
                  style={{ backgroundColor: bgColor, height: '28px' }}
                >
                  {status ? (
                    <span style={{ color: STATUS_COLOR[status] || '#fff', fontSize: '13px' }}>
                      {status === 'sending' ? '…' : STATUS_ICON[status]}
                    </span>
                  ) : (
                    carNumber
                  )}
                  {!status && isRedFlag && (
                    <span className="material-symbols-outlined absolute right-0.5 text-[10px] animate-pulse">flag</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {!isReadOnly && (
        <div style={{ padding: '8px', borderTop: '1px solid #2e3148', background: '#1a1c2c', flexShrink: 0 }}>
          {doneCount > 0 && doneCount === totalSent && (
            <div style={{ fontSize: '11px', textAlign: 'center', marginBottom: '6px', color: errorCount > 0 ? '#f87171' : '#4ade80' }}>
              {sentCount > 0 && `✓ Sent: ${sentCount}`}
              {errorCount > 0 && `  ✕ Failed: ${errorCount}`}
            </div>
          )}
          <button
            onClick={handleSendRedFlag}
            disabled={isSending || devices.length === 0}
            style={{
              width: '100%',
              padding: '7px',
              background: isSending ? '#7f1d1d' : '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              fontWeight: 700,
              fontSize: '12px',
              cursor: isSending ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              opacity: isSending ? 0.7 : 1,
              transition: 'background 0.15s',
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>flag</span>
            {isSending ? 'Sending…' : 'Send Red Flag'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AreaSelectionPopup;
